/**
 * Booking-Based Occupancy Service
 *
 * Fetches real booking data from WebHotelier Booking Pull API
 * and calculates accurate occupancy based on actual reservations.
 *
 * This replaces estimated occupancy with real data.
 */

import { supabase } from './supabaseClient';
import { getWebHotelierProxyService } from '../features/webhotelier/services/webHotelierProxyService';
import { gapDetectionService } from './gapDetectionService';
import {
  BookingDetails,
  BookingSearchFilters,
  AvailabilityResponse,
  DailyAvailability,
} from '../types/webHotelier';
import { PRICING_CONFIG } from '../config/pricingConfig';

type RoomTypeAvailability = DailyAvailability['roomTypes'][string];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function isSameReservationValue(stored: unknown, incoming: unknown): boolean {
  if (incoming === undefined) return true;
  if (stored === null || stored === undefined || incoming === null)
    return (stored ?? null) === incoming;
  if (typeof stored === 'number' || typeof incoming === 'number')
    return Number(stored) === Number(incoming);
  if (typeof stored === 'string' && ISO_DATE.test(stored))
    return stored === String(incoming).slice(0, 10);
  return String(stored) === String(incoming);
}

export function hasReservationChanged(
  existing: Record<string, unknown>,
  update: Record<string, unknown>
): boolean {
  return Object.entries(update).some(
    ([column, value]) => !isSameReservationValue(existing[column], value)
  );
}

export type ReservationStatus =
  | 'confirmed'
  | 'pending'
  | 'cancelled'
  | 'checked_in'
  | 'checked_out'
  | 'no_show';

export type SyncType = 'full' | 'incremental' | 'inventory';

export type SyncStatus = 'running' | 'success' | 'failure' | 'partial';

export interface Reservation {
  id: string;
  property_id: string;
  room_type_id?: string;
  webhotelier_room_code: string;
  confirmation_number: string;
  booking_id?: string;
  status: ReservationStatus;
  check_in: string;
  check_out: string;
  nights: number;
  adults: number;
  children: number;
  rooms: number;
  total_amount?: number;
  currency: string;
  guest_name?: string;
  guest_email?: string;
  source: string;
  synced_at: string;
}

export interface OccupancyResult {
  date: string;
  roomTypeCode: string;
  bookedRooms: number;
  totalRooms: number;
  occupancyPercentage: number;
  source: 'booking_pull';
}

export interface BookingSyncResult {
  success: boolean;
  syncType: SyncType;
  bookingsFetched: number;
  bookingsCreated: number;
  bookingsUpdated: number;
  bookingsCancelled: number;
  inventoryUpdated: number;
  errors: string[];
  duration: number;
}

export interface RoomInventory {
  roomTypeCode: string;
  roomTypeName: string;
  totalRooms: number;
  propertyId: string;
  baseRate: number;
}

type BookingChangeCallback = (
  propertyId: string,
  result: BookingSyncResult
) => void;

class BookingOccupancyService {
  private get webHotelierService() {
    return getWebHotelierProxyService();
  }

  private onChangeCallbacks = new Set<BookingChangeCallback>();

  /**
   * Subscribe to booking changes.
   * Callbacks are invoked after sync completes when bookings were created, updated, or cancelled.
   * @returns Unsubscribe function
   */
  subscribeToChanges(callback: BookingChangeCallback): () => void {
    this.onChangeCallbacks.add(callback);
    return () => {
      this.onChangeCallbacks.delete(callback);
    };
  }

  /**
   * Notify all subscribers of booking changes
   */
  private notifyChanges(propertyId: string, result: BookingSyncResult): void {
    const hasChanges =
      result.bookingsCreated > 0 ||
      result.bookingsUpdated > 0 ||
      result.bookingsCancelled > 0;

    if (!hasChanges) {
      return;
    }

    console.log(
      `[BookingOccupancyService] Notifying ${this.onChangeCallbacks.size} subscribers of booking changes`
    );

    this.onChangeCallbacks.forEach((cb) => {
      try {
        cb(propertyId, result);
      } catch (error) {
        console.error(
          '[BookingOccupancyService] Error in booking change callback:',
          error
        );
      }
    });
  }

  /**
   * Sync bookings from WebHotelier and calculate real occupancy
   */
  async syncBookingsAndOccupancy(
    propertyId: string,
    propertyCode: string,
    options: {
      syncType?: SyncType;
      startDate?: Date;
      endDate?: Date;
      forceFullSync?: boolean;
    } = {}
  ): Promise<BookingSyncResult> {
    const startTime = Date.now();
    const syncType = options.syncType || 'incremental';
    const errors: string[] = [];

    let bookingsFetched = 0;
    let bookingsCreated = 0;
    let bookingsUpdated = 0;
    let bookingsCancelled = 0;
    let inventoryUpdated = 0;

    // Log sync start
    const { data: syncLog } = await supabase
      .from('booking_sync_logs')
      .insert({
        property_id: propertyId,
        sync_type: syncType,
        status: 'running' as SyncStatus,
      })
      .select('id')
      .single();

    const syncLogId = syncLog?.id;

    try {
      // Step 1: Fetch room inventory first
      console.log('[BookingOccupancyService] Fetching room inventory...');
      const inventoryResult = await this.syncRoomInventory(
        propertyId,
        propertyCode
      );
      if (inventoryResult.success) {
        inventoryUpdated = inventoryResult.updated;
      } else {
        errors.push(`Inventory sync failed: ${inventoryResult.error}`);
      }

      // Step 2: Determine date range for booking fetch
      const now = new Date();
      const startDate =
        options.startDate || new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago
      const endDate =
        options.endDate ||
        new Date(
          now.getTime() +
            PRICING_CONFIG.MAX_LOOK_AHEAD_DAYS * 24 * 60 * 60 * 1000
        ); // 365 days ahead

      // Step 3: Fetch bookings from WebHotelier
      console.log(
        '[BookingOccupancyService] Fetching bookings from WebHotelier...'
      );

      if (!this.webHotelierService) {
        throw new Error('WebHotelier service not available');
      }

      // Try pull bookings first (for incremental sync) - but ALWAYS fall back to search
      let bookings: BookingDetails[] = [];
      let usedPullEndpoint = false;

      if (syncType === 'incremental' && !options.forceFullSync) {
        // Get last sync time
        const lastSync = await this.getLastSyncTime(propertyId);
        console.log(
          '[BookingOccupancyService] Attempting incremental sync, last sync:',
          lastSync?.toISOString() || 'never'
        );

        const pullResult = await this.webHotelierService.pullBookings(
          lastSync || undefined
        );

        if (pullResult.success && pullResult.data) {
          bookings = pullResult.data.bookings || [];
          bookingsFetched = bookings.length;
          usedPullEndpoint = true;
          console.log(
            `[BookingOccupancyService] Pull endpoint returned ${bookingsFetched} pending bookings`
          );
        } else {
          console.warn(
            '[BookingOccupancyService] Pull bookings failed:',
            pullResult.error?.message
          );
        }
      }

      // Search for bookings. (Prices/availability for calendar_data are no
      // longer fetched here — WebHotelier `allot` via the availability sync is
      // the source of truth; see calculateAndStoreOccupancy.)
      console.log('[BookingOccupancyService] Searching bookings...');

      const searchFilters: BookingSearchFilters = {
        propertyCode,
        checkinFrom: this.formatDate(startDate),
        checkinTo: this.formatDate(endDate),
        status: ['confirmed', 'pending'],
        limit: 500,
      };

      const searchResult =
        await this.webHotelierService.searchBookings(searchFilters);

      if (searchResult.success && searchResult.data) {
        const searchedBookings = searchResult.data.bookings || [];
        console.log(
          `[BookingOccupancyService] Search returned ${searchedBookings.length} bookings`
        );

        // Merge bookings: pulled bookings take priority (they have latest status)
        if (usedPullEndpoint && bookings.length > 0) {
          const pulledIds = new Set(
            bookings.map((b) => b.confirmation_number || b.booking_id)
          );
          for (const searchedBooking of searchedBookings) {
            const searchedId =
              searchedBooking.confirmation_number || searchedBooking.booking_id;
            if (!pulledIds.has(searchedId)) {
              bookings.push(searchedBooking);
            }
          }
          console.log(
            `[BookingOccupancyService] After merge: ${bookings.length} total bookings`
          );
        } else {
          bookings = searchedBookings;
        }
        bookingsFetched = bookings.length;
      } else {
        console.error(
          '[BookingOccupancyService] Search bookings failed:',
          searchResult.error?.message
        );
        errors.push(`Failed to fetch bookings: ${searchResult.error?.message}`);
      }

      // Step 4: Process bookings in PARALLEL batches (max 10 concurrent)
      console.log(
        '[BookingOccupancyService] Processing bookings in parallel...'
      );

      const BATCH_SIZE = 10;
      for (let i = 0; i < bookings.length; i += BATCH_SIZE) {
        const batch = bookings.slice(i, i + BATCH_SIZE);
        const results = await Promise.all(
          batch.map(async (booking) => {
            try {
              return await this.upsertReservation(propertyId, booking);
            } catch (err) {
              errors.push(
                `Failed to process booking ${booking.confirmation_number}: ${err}`
              );
              return { created: false, updated: false, cancelled: false };
            }
          })
        );
        for (const result of results) {
          if (result.created) bookingsCreated++;
          if (result.updated) bookingsUpdated++;
          if (result.cancelled) bookingsCancelled++;
        }
      }

      // Step 6: Calculate and store occupancy data
      console.log(
        '[BookingOccupancyService] Calculating occupancy from reservations...'
      );
      await this.calculateAndStoreOccupancy(propertyId, startDate, endDate);

      // Step 7: Detect gaps in reservations (run in background, don't block sync)
      gapDetectionService
        .detectGaps(propertyId, undefined, {
          minGapDays: 1,
          maxGapDays: 7,
          lookAheadDays: 90,
        })
        .then((gapResult) => {
          if (gapResult.success && gapResult.gaps) {
            console.log(
              `[BookingOccupancyService] Detected ${gapResult.gaps.length} gaps`
            );
          }
        })
        .catch((gapError) => {
          console.warn(
            '[BookingOccupancyService] Gap detection failed:',
            gapError
          );
        });

      // Update sync log
      if (syncLogId) {
        await supabase
          .from('booking_sync_logs')
          .update({
            status: errors.length === 0 ? 'success' : ('partial' as SyncStatus),
            bookings_fetched: bookingsFetched,
            bookings_created: bookingsCreated,
            bookings_updated: bookingsUpdated,
            bookings_cancelled: bookingsCancelled,
            inventory_updated: inventoryUpdated,
            errors: errors,
            completed_at: new Date().toISOString(),
            duration_ms: Date.now() - startTime,
          })
          .eq('id', syncLogId);
      }

      const result: BookingSyncResult = {
        success: errors.length === 0,
        syncType,
        bookingsFetched,
        bookingsCreated,
        bookingsUpdated,
        bookingsCancelled,
        inventoryUpdated,
        errors,
        duration: Date.now() - startTime,
      };

      // Notify subscribers of booking changes
      this.notifyChanges(propertyId, result);

      return result;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      errors.push(errorMessage);

      // Update sync log with failure
      if (syncLogId) {
        await supabase
          .from('booking_sync_logs')
          .update({
            status: 'failure' as SyncStatus,
            errors: errors,
            completed_at: new Date().toISOString(),
            duration_ms: Date.now() - startTime,
          })
          .eq('id', syncLogId);
      }

      const result: BookingSyncResult = {
        success: false,
        syncType,
        bookingsFetched,
        bookingsCreated,
        bookingsUpdated,
        bookingsCancelled,
        inventoryUpdated,
        errors,
        duration: Date.now() - startTime,
      };

      // Still notify subscribers of any partial changes that occurred before the error
      this.notifyChanges(propertyId, result);

      return result;
    }
  }

  /**
   * Sync room inventory from WebHotelier
   */
  async syncRoomInventory(
    propertyId: string,
    propertyCode: string
  ): Promise<{ success: boolean; updated: number; error?: string }> {
    try {
      if (!this.webHotelierService) {
        return {
          success: false,
          updated: 0,
          error: 'WebHotelier service not available',
        };
      }

      // Fetch availability to get room inventory
      // The availability endpoint with no date filter or a future date should return all room types with availability
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const weekLater = new Date();
      weekLater.setDate(weekLater.getDate() + 7);

      const availResult = await this.webHotelierService.getAvailability({
        propertyCode,
        checkin: this.formatDate(tomorrow),
        checkout: this.formatDate(weekLater),
        adults: 1,
        rooms: 1,
      });

      if (!availResult.success || !availResult.data) {
        return {
          success: false,
          updated: 0,
          error: 'Failed to fetch availability data',
        };
      }

      // Extract room inventory from availability response
      const responseData = availResult.data as AvailabilityResponse & {
        data?: { rates?: AvailabilityResponse['rates'] };
      };
      const rates = responseData.data?.rates || responseData.rates || [];

      // Group by room type and find max availability (which indicates total inventory)
      const inventoryMap = new Map<string, RoomInventory>();

      for (const rate of rates) {
        const roomCode = rate.type || rate.roomtype?.code;
        const roomName = rate.room || rate.roomtype?.name || roomCode;
        const price = rate.pricing?.price || rate.price || 100; // Default base rate if not available

        if (roomCode) {
          const existing = inventoryMap.get(roomCode);
          if (!existing) {
            // Don't use 'remaining' as totalRooms - that's available rooms, not total inventory
            // Set totalRooms to 0 initially; the calendar endpoint or DB will provide the real value
            inventoryMap.set(roomCode, {
              roomTypeCode: roomCode,
              roomTypeName: roomName,
              totalRooms: 0, // Will be set by calendar endpoint or preserved from database
              propertyId,
              baseRate: price,
            });
          }
        }
      }

      // Also try the calendar endpoint for more accurate inventory
      try {
        const calendarResult =
          await this.webHotelierService.getAvailabilityCalendar({
            propertyCode,
            from: this.formatDate(tomorrow),
            to: this.formatDate(weekLater),
          });

        if (calendarResult.success && calendarResult.data?.calendar) {
          for (const day of calendarResult.data.calendar) {
            for (const [roomCode, roomData] of Object.entries(day.roomTypes)) {
              const typedRoomData = roomData as RoomTypeAvailability;
              const total = typedRoomData.total || 0;
              const existing = inventoryMap.get(roomCode);
              // Get price from first rate if available
              const roomRates = typedRoomData.rates || {};
              const firstRate = Object.values(roomRates)[0] as
                | { price: number; available: boolean }
                | undefined;
              const price = firstRate?.price || existing?.baseRate || 100;

              // Calendar endpoint provides actual 'total' inventory - always use it when available
              if (total > 0) {
                inventoryMap.set(roomCode, {
                  roomTypeCode: roomCode,
                  roomTypeName: existing?.roomTypeName || roomCode,
                  totalRooms: total,
                  propertyId,
                  baseRate: price,
                });
              }
            }
          }
        }
      } catch (_calendarErr) {
        console.warn(
          '[BookingOccupancyService] Calendar endpoint failed, using availability data'
        );
      }

      // Upsert room_types table with inventory (create if not exists)
      // IMPORTANT: Never overwrite user-configured total_rooms values
      let updated = 0;
      for (const [roomCode, inventory] of inventoryMap) {
        // First try to find existing room type WITH its total_rooms
        const { data: existingRoom } = await supabase
          .from('room_types')
          .select('id, total_rooms')
          .eq('property_id', propertyId)
          .or(
            `webhotelier_code.eq.${roomCode},name.eq.${inventory.roomTypeName}`
          )
          .maybeSingle();

        if (existingRoom) {
          // Always sync total_rooms from WebHotelier when valid (> 0)
          // This ensures occupancy calculations use correct inventory
          const updateData: Record<string, unknown> = {
            webhotelier_code: roomCode,
            last_inventory_sync: new Date().toISOString(),
          };

          console.log(
            `[BookingOccupancyService] Room ${roomCode}: WebHotelier total=${inventory.totalRooms}, DB total=${existingRoom.total_rooms}`
          );

          if (inventory.totalRooms > 0) {
            updateData.total_rooms = inventory.totalRooms;
            console.log(
              `[BookingOccupancyService] Syncing total_rooms for ${roomCode}: ${inventory.totalRooms} (from WebHotelier)`
            );
          }

          const { error } = await supabase
            .from('room_types')
            .update(updateData)
            .eq('id', existingRoom.id);

          if (!error) updated++;
        } else {
          // Create new room type with all required columns
          const baseRateValue = Number(inventory.baseRate) || 100;
          console.log(
            `[BookingOccupancyService] Creating new room type: ${roomCode} (${inventory.roomTypeName}) with base_rate: ${baseRateValue}`
          );
          const { error } = await supabase.from('room_types').insert({
            property_id: propertyId,
            name: inventory.roomTypeName,
            code: roomCode,
            webhotelier_code: roomCode,
            total_rooms: inventory.totalRooms || 1,
            base_rate: baseRateValue,
            capacity: 2, // Default capacity
            last_inventory_sync: new Date().toISOString(),
          });

          if (!error) {
            updated++;
            console.log(
              `[BookingOccupancyService] Successfully created room type: ${roomCode}`
            );
          } else {
            console.error(
              `[BookingOccupancyService] Failed to create room type ${roomCode}:`,
              error.message,
              error.details
            );
          }
        }
      }

      console.log(
        `[BookingOccupancyService] Updated/created ${updated} room types`
      );
      return { success: true, updated };
    } catch (error) {
      return {
        success: false,
        updated: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * Upsert a reservation from WebHotelier booking data
   */
  private async upsertReservation(
    propertyId: string,
    booking: BookingDetails
  ): Promise<{ created: boolean; updated: boolean; cancelled: boolean }> {
    // Handle various ID formats from WebHotelier API
    // Store the WebHotelier booking ID in booking_id column (TEXT)
    const webHotelierBookingId =
      booking.booking_id ||
      booking.confirmation_number ||
      (booking as unknown as { id?: string }).id;

    if (!webHotelierBookingId || webHotelierBookingId === 'undefined') {
      console.warn(
        '[BookingOccupancyService] Booking has no valid ID, skipping:',
        booking
      );
      return { created: false, updated: false, cancelled: false };
    }

    // Map WebHotelier status to our status
    const statusMap: Record<string, ReservationStatus> = {
      confirmed: 'confirmed',
      pending: 'pending',
      cancelled: 'cancelled',
    };

    const status = statusMap[booking.status] || 'confirmed';
    const isCancelled = status === 'cancelled';

    // Check if reservation exists by confirmation_number (has GLOBAL unique constraint)
    // Don't filter by property_id since the constraint is global
    const { data: existing } = await supabase
      .from('reservations')
      .select(
        'id, status, property_id, confirmation_number, check_in, check_out, source, webhotelier_room_code, adults, children, rooms, total_amount, currency'
      )
      .eq('confirmation_number', String(webHotelierBookingId))
      .maybeSingle();

    // Extract check-in/check-out dates - may be at top level or in roomStay
    const checkIn =
      booking.checkin ||
      (booking as Record<string, unknown>).check_in ||
      (booking.room_type as Record<string, unknown>)?.checkin;
    const checkOut =
      booking.checkout ||
      (booking as Record<string, unknown>).check_out ||
      (booking.room_type as Record<string, unknown>)?.checkout;

    console.log(
      `[BookingOccupancyService] Reservation ${webHotelierBookingId} dates:`,
      {
        bookingCheckin: booking.checkin,
        bookingCheckout: booking.checkout,
        resolvedCheckIn: checkIn,
        resolvedCheckOut: checkOut,
      }
    );

    // Build reservation data with only columns that exist in the table
    const reservationData: Record<string, unknown> = {
      property_id: propertyId,
      confirmation_number: String(webHotelierBookingId),
      status,
      check_in: checkIn,
      check_out: checkOut,
      source: booking.source || 'webhotelier',
      guest_name: booking.guest
        ? `${booking.guest.first_name} ${booking.guest.last_name}`.trim()
        : null,
    };

    // Add optional fields if they have values
    // Try multiple possible room code locations from WebHotelier API
    const roomCode =
      booking.room_type?.code ||
      booking.room_type?.type ||
      (booking as Record<string, unknown>).room_code ||
      (booking as Record<string, unknown>).roomtype;
    if (roomCode) {
      reservationData.webhotelier_room_code = roomCode;
    }
    console.log(
      `[BookingOccupancyService] Reservation ${webHotelierBookingId} room code:`,
      {
        roomTypeCode: booking.room_type?.code,
        roomTypeType: booking.room_type?.type,
        resolvedCode: roomCode,
        fullRoomType: booking.room_type,
        checkin: booking.checkin,
        checkout: booking.checkout,
      }
    );
    if (booking.adults) reservationData.adults = booking.adults;
    if (booking.children !== undefined)
      reservationData.children = booking.children;
    if (booking.rooms) reservationData.rooms = booking.rooms;
    if (booking.total_amount)
      reservationData.total_amount = booking.total_amount;
    if (booking.currency) reservationData.currency = booking.currency;
    if (booking.guest?.email) reservationData.guest_email = booking.guest.email;

    if (existing) {
      // Update existing reservation
      // Also ensure property_id is updated if it was different/null
      if (existing.property_id !== propertyId) {
        console.log(
          `[BookingOccupancyService] Updating reservation ${existing.id} property_id: ${existing.property_id} -> ${propertyId}`
        );
      }
      console.log(
        `[BookingOccupancyService] Updating existing reservation ${existing.id} (confirmation: ${webHotelierBookingId})`
      );
      // Guest PII is already encrypted into Vault by the
      // encrypt_reservation_guest_pii trigger (the row at rest has
      // guest_name/guest_email = NULL). Re-sending plaintext on every sync
      // makes that trigger re-encrypt and create a NEW Vault secret each time
      // — wasted work, orphan secrets, and (before the UUID fix) name
      // collisions. The booking sync only needs to refresh status/dates/rooms,
      // so strip PII from the update payload.
      const updateData = { ...reservationData };
      delete updateData.guest_name;
      delete updateData.guest_email;

      // Every sync re-reads the whole booking window. Reporting unchanged rows
      // as "updated" made each page load fire a full 365-day forced reprice.
      if (!hasReservationChanged(existing, updateData)) {
        return { created: false, updated: false, cancelled: false };
      }

      const { error } = await supabase
        .from('reservations')
        .update(updateData)
        .eq('id', existing.id);

      if (error) {
        console.error(
          '[BookingOccupancyService] Failed to update reservation:',
          error.message
        );
      }

      return {
        created: false,
        updated: true,
        cancelled: isCancelled && existing.status !== 'cancelled',
      };
    } else {
      // Insert new reservation (let Supabase generate UUID for id)
      console.log(
        `[BookingOccupancyService] Inserting new reservation (confirmation: ${webHotelierBookingId})`
      );
      const { error } = await supabase
        .from('reservations')
        .insert(reservationData);

      if (error) {
        console.error(
          '[BookingOccupancyService] Failed to insert reservation:',
          {
            code: error.code,
            message: error.message,
            details: error.details,
          }
        );
        return { created: false, updated: false, cancelled: false };
      }

      return {
        created: true,
        updated: false,
        cancelled: isCancelled,
      };
    }
  }

  /**
   * Calculate occupancy from reservations and store in occupancy_data
   * Optimized: Uses batch operations instead of individual queries
   */
  async calculateAndStoreOccupancy(
    propertyId: string,
    startDate: Date,
    endDate: Date
  ): Promise<void> {
    const startTime = Date.now();

    // Get room types with inventory
    const { data: roomTypes } = await supabase
      .from('room_types')
      .select('id, name, webhotelier_code, total_rooms')
      .eq('property_id', propertyId);

    if (!roomTypes || roomTypes.length === 0) {
      console.warn(
        '[BookingOccupancyService] No room types found for property'
      );
      return;
    }

    // Fetch ALL reservations for this property in the date range (single query)
    // We fetch all statuses and filter out cancelled ones in code for case-insensitivity
    const { data: allReservations } = await supabase
      .from('reservations')
      .select('webhotelier_room_code, check_in, check_out, rooms, status')
      .eq('property_id', propertyId)
      .lte('check_in', this.formatDate(endDate))
      .gte('check_out', this.formatDate(startDate));

    // Helper to check if a reservation is cancelled (case-insensitive)
    const isCancelled = (status: string | null): boolean => {
      if (!status) return false;
      const s = status.toLowerCase();
      return (
        s === 'cancelled' ||
        s.includes('cancel') ||
        s === 'void' ||
        s === 'no_show'
      );
    };

    // Build a map of room code -> reservation list for quick lookup
    // Only include non-cancelled reservations
    const reservationsByRoom = new Map<string, typeof allReservations>();
    for (const res of allReservations || []) {
      if (isCancelled(res.status)) continue; // Skip cancelled reservations
      const code = res.webhotelier_room_code;
      if (!reservationsByRoom.has(code)) {
        reservationsByRoom.set(code, []);
      }
      reservationsByRoom.get(code)!.push(res);
    }

    // Count active (non-cancelled) reservations
    const activeReservationCount = Array.from(
      reservationsByRoom.values()
    ).reduce((sum, list) => sum + list.length, 0);

    // Debug: Log reservation room codes and room types
    console.log('[BookingOccupancyService] Reservation room codes found:', {
      totalReservations: allReservations?.length || 0,
      activeReservations: activeReservationCount,
      cancelledCount: (allReservations?.length || 0) - activeReservationCount,
      uniqueRoomCodes: Array.from(reservationsByRoom.keys()),
      roomTypeCodes: roomTypes.map((rt) => ({
        id: rt.id,
        code: rt.webhotelier_code,
        name: rt.name,
      })),
    });

    // Generate all occupancy records in memory
    const occupancyRecords: Array<{
      property_id: string;
      room_type_id: string;
      date: string;
      total_rooms: number;
      occupied_rooms: number;
      available_rooms: number;
      forecasted: boolean;
    }> = [];

    const today = new Date();
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = this.formatDate(currentDate);

      for (const roomType of roomTypes) {
        const roomCode = roomType.webhotelier_code || roomType.name;
        const totalRooms = roomType.total_rooms || 1;
        const roomReservations = reservationsByRoom.get(roomCode) || [];

        // Count booked rooms for this date (reservation overlaps if check_in <= date < check_out)
        let bookedRooms = 0;
        for (const res of roomReservations) {
          if (res.check_in <= dateStr && res.check_out > dateStr) {
            bookedRooms += res.rooms || 1;
          }
        }

        // Cap occupied_rooms at total_rooms to prevent overbookings
        // total_rooms is synced from WebHotelier to ensure accuracy
        const cappedOccupiedRooms = Math.min(bookedRooms, totalRooms);

        if (bookedRooms > totalRooms) {
          console.warn(
            `[BookingOccupancyService] Overbooking prevented: ${roomCode} on ${dateStr} has ${bookedRooms} bookings but only ${totalRooms} rooms - capping at ${totalRooms}`
          );
        }

        occupancyRecords.push({
          property_id: String(propertyId),
          room_type_id: String(roomType.id),
          date: dateStr,
          total_rooms: totalRooms,
          occupied_rooms: cappedOccupiedRooms,
          available_rooms: Math.max(0, totalRooms - cappedOccupiedRooms),
          forecasted: currentDate > today,
        });
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Batch upsert to occupancy_data (single database call)
    if (occupancyRecords.length > 0) {
      const { error: upsertError } = await supabase
        .from('occupancy_data')
        .upsert(occupancyRecords, {
          onConflict: 'property_id,room_type_id,date',
        });

      if (upsertError) {
        console.error(
          '[BookingOccupancyService] Batch occupancy upsert error:',
          upsertError.message
        );
      }

      // NOTE: calendar_data occupancy/availability is intentionally NOT written
      // here. WebHotelier `allot` (written by the availability sync /
      // webhotelier-sync-worker) is the single source of truth for
      // available_rooms / booked_rooms / occupancy_percentage, so it reflects
      // closeouts and stop-sells — not just confirmed bookings. `allot` already
      // drops by 1 when a booking comes in, so occupancy still tracks bookings
      // (and the occupancy pricing rules still fire) while staying accurate on
      // closed dates. The booking sync only refreshes the `reservations` and
      // `occupancy_data` tables (above); it must never overwrite allot-derived
      // calendar_data occupancy.
    }

    const duration = Date.now() - startTime;
    console.log(
      `[BookingOccupancyService] Updated ${occupancyRecords.length} occupancy records in ${duration}ms`
    );
  }

  /**
   * Get occupancy data for a date range
   */
  async getOccupancy(
    propertyId: string,
    startDate: Date,
    endDate: Date,
    roomTypeCode?: string
  ): Promise<OccupancyResult[]> {
    // Query only columns that exist in the table
    const query = supabase
      .from('occupancy_data')
      .select(
        `
        date,
        total_rooms,
        occupied_rooms,
        room_type_id,
        room_types(webhotelier_code, name)
      `
      )
      .eq('property_id', propertyId)
      .gte('date', this.formatDate(startDate))
      .lte('date', this.formatDate(endDate))
      .order('date');

    const { data, error } = await query;

    if (error) {
      console.error(
        '[BookingOccupancyService] Error fetching occupancy:',
        error.message
      );
      return [];
    }

    if (!data || data.length === 0) {
      return [];
    }

    type OccupancyRow = {
      date: string;
      total_rooms: number;
      occupied_rooms: number;
      room_type_id: string;
      room_types: {
        webhotelier_code: string | null;
        name: string | null;
      } | null;
    };

    // Filter by room type code if specified
    let filteredData = data as OccupancyRow[];
    if (roomTypeCode) {
      filteredData = (data as OccupancyRow[]).filter(
        (row) =>
          row.room_types?.webhotelier_code === roomTypeCode ||
          row.room_types?.name === roomTypeCode
      );
    }

    // Calculate occupancy percentage in code (column doesn't exist in DB)
    return filteredData.map((row) => ({
      date: row.date,
      roomTypeCode:
        row.room_types?.webhotelier_code ||
        row.room_types?.name ||
        row.room_type_id,
      bookedRooms: row.occupied_rooms,
      totalRooms: row.total_rooms,
      occupancyPercentage:
        row.total_rooms > 0 ? (row.occupied_rooms / row.total_rooms) * 100 : 0,
      source: 'booking_pull' as const,
    }));
  }

  /**
   * Get aggregated occupancy for a property (all room types combined)
   */
  async getAggregatedOccupancy(
    propertyId: string,
    startDate: Date,
    endDate: Date
  ): Promise<
    Map<string, { booked: number; total: number; occupancy: number }>
  > {
    const { data } = await supabase
      .from('occupancy_data')
      .select('date, total_rooms, occupied_rooms')
      .eq('property_id', propertyId)
      .gte('date', this.formatDate(startDate))
      .lte('date', this.formatDate(endDate));

    const result = new Map<
      string,
      { booked: number; total: number; occupancy: number }
    >();

    if (!data) return result;

    // Aggregate by date
    for (const row of data) {
      const existing = result.get(row.date) || {
        booked: 0,
        total: 0,
        occupancy: 0,
      };
      existing.booked += row.occupied_rooms || 0;
      existing.total += row.total_rooms || 0;
      existing.occupancy =
        existing.total > 0 ? (existing.booked / existing.total) * 100 : 0;
      result.set(row.date, existing);
    }

    return result;
  }

  /**
   * Get last successful sync time
   */
  private async getLastSyncTime(propertyId: string): Promise<Date | null> {
    const { data } = await supabase
      .from('booking_sync_logs')
      .select('completed_at')
      .eq('property_id', propertyId)
      .eq('status', 'success')
      .order('completed_at', { ascending: false })
      .limit(1)
      .single();

    return data?.completed_at ? new Date(data.completed_at) : null;
  }

  /**
   * Format date as YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}

// Export singleton
export const bookingOccupancyService = new BookingOccupancyService();
